import React, { useState } from "react";
import userContext from "./userContext";
import API_BASE from "../../apiConfig";

// ── Permission matrix (mirrors backend requireRole.js) ─
const PERMISSIONS = {
  superadmin: { all: "write" },
  owner: { all: "write" },
  admin: { all: "write" },
  accountant: {
    dashboard: "read", clients: "read", invoices: "write",
    ledger: "write", bills: "write", gst: "write",
    reports: "read", tds: "write", expenses: "write", activity: "read",
  },
  sales: {
    dashboard: "read", clients: "write", invoices: "write", activity: "read",
  },
  inventory: {
    dashboard: "read", stock: "write", products: "write",
    purchases: "write", suppliers: "write", activity: "read",
  },
  hr: {
    dashboard: "read", staff: "write", attendance: "write",
    payroll: "write", activity: "read",
  },
  viewer: {
    dashboard: "read", clients: "read", invoices: "read",
    ledger: "read", bills: "read", gst: "read", reports: "read",
    stock: "read", products: "read", purchases: "read",
    suppliers: "read", staff: "read", attendance: "read",
    payroll: "read", expenses: "read", tds: "read", activity: "read",
  },
};

// ── Route → module mapping ─────────────────────────────
// Maps dashboard route paths to permission module names
const ROUTE_MODULE_MAP = {
  "home": "dashboard",
  "manageclients": "clients",
  "createinvoice": "invoices",
  "viewinvoices": "invoices",
  "ledgerpage": "ledger",
  "expenses": "expenses",
  "managestock": "stock",
  "reports": "reports",
  "importproducts": "stock",
  "sales/salespeople": "sales",
  "sales/commissions": "sales",
  "tdsreceivable": "tds",
  "gst-reports": "gst",
  "purchase-orders": "purchases",
  "purchase-entry": "purchases",
  "managesuppliers": "suppliers",
  "financial-reports": "reports",
  "staff": "staff",
  "staff/attendance": "attendance",
  "staff/payroll": "payroll",
  "bills": "bills",
  "credit-notes": "invoices",
  "debit-notes": "purchases",
  "recurring-invoices": "invoices",
  "supplier-ledger": "suppliers",
  "addbankdetails": "dashboard",
  "profile": "dashboard",
  "accounts": "dashboard",
  "team": "team",
};

/**
 * Check if a role has permission for a module + action
 */
const checkPermission = (role, module_, action = "read") => {
  if (!role) return false;
  const perms = PERMISSIONS[role];
  if (!perms) return false;
  if (perms.all === "write") return true; // owner + admin
  const perm = perms[module_];
  if (!perm) return false;
  if (action === "read") return perm === "read" || perm === "write";
  if (action === "write") return perm === "write";
  return false;
};

// ══════════════════════════════════════════════════════
// UserState
// ══════════════════════════════════════════════════════
const UserState = (props) => {
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  // ── Fetch logged-in user ───────────────────────────
  const getUser = async () => {
    setUserLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/getuser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "auth-token": localStorage.getItem("token"),
        },
      });
      const data = await res.json();
      if (res.ok) {
        setUser(data);
      } else {
        console.error("Failed to fetch user:", data.error);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
    } finally {
      setUserLoading(false);
    }
  };

  // ── Update user details ────────────────────────────
  const updateUser = async (updates) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/updateuser`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "auth-token": localStorage.getItem("token"),
        },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (res.ok) {
        setUser(data);
        return data;
      } else {
        console.error("Failed to update user:", data.error);
        throw new Error(data.error || "Update failed");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      throw err;
    }
  };

  // ── Permission helpers ─────────────────────────────

  /**
   * Check if current user can access a module
   * Usage: hasPermission("invoices", "write")
   */
  const hasPermission = (module_, action = "read") => {
    if (!user) return false;
    return checkPermission(user.role, module_, action);
  };

  /**
   * Check if current user can access a dashboard route path
   * Usage: canAccessRoute("staff/payroll")
   */
  const canAccessRoute = (routePath) => {
    if (!user) return false;
    if (user.role === "owner" || user.role === "admin" || user.role === "superadmin") return true;
    const module_ = ROUTE_MODULE_MAP[routePath];
    if (!module_) return true;
    return checkPermission(user.role, module_, "read");
  };

  /**
   * Check if current user is owner or admin
   */
  const isAdmin = () => {
    if (!user) return false;
    return user.role === "owner" || user.role === "admin" || user.role === "superadmin";
  };

  /**
   * Check if current user is owner
   */
  const isOwner = () => {
    if (!user) return false;
    return user.role === "owner";
  };

  /**
   * Get all routes the current user can access
   * Used by navbar to show/hide menu items
   */
  const accessibleRoutes = () => {
    if (!user) return [];
    if (user.role === "owner" || user.role === "admin") {
      return Object.keys(ROUTE_MODULE_MAP);
    }
    return Object.keys(ROUTE_MODULE_MAP).filter(route =>
      canAccessRoute(route)
    );
  };

  return (
    <userContext.Provider value={{
      user,
      getUser,
      updateUser,
      userLoading,

      // Permission helpers
      hasPermission,
      canAccessRoute,
      isAdmin,
      isOwner,
      accessibleRoutes,

      // Expose for components that need raw check
      PERMISSIONS,
      ROUTE_MODULE_MAP,
    }}>
      {props.children}
    </userContext.Provider>
  );
};

export default UserState;